import { useMemo } from 'react'

import { type EntityDescriptor, type EntitySetVerdict, checkEntitySetAccess } from '@/utils/access-control/perEntity'
import { usePermissions } from '@/utils/permissions'

export type EntitySetAccessResult =
	| { allowed: true; verdict: EntitySetVerdict }
	| { allowed: false; reason: 'no-entity-permission'; verdict: EntitySetVerdict }

/**
 * Per-entity-set RBAC primitive — the bulk counterpart to `useEntityAccess`.
 *
 * Reads the current platform user's permissions and runs `checkEntitySetAccess`.
 *
 * Default gating semantics: a bulk action is allowed iff every entity in the
 * set passes its individual check (`verdict.allAllowed`). Callers that need
 * "any" or "count" semantics can read the verdict directly.
 *
 * Returns `{ allowed: true, verdict }` when no `entities` is supplied or the
 * set is empty — keeps the call site simple for components that conditionally
 * compute a set.
 */
export function useEntitySetAccess(entities?: EntityDescriptor[]): EntitySetAccessResult {
	const permissions = usePermissions()

	return useMemo((): EntitySetAccessResult => {
		const list = entities ?? []
		if (list.length === 0) {
			return {
				allowed: true,
				verdict: { allAllowed: true, allowedCount: 0, deniedCount: 0, total: 0 },
			}
		}

		const verdict = checkEntitySetAccess(permissions, list)
		return verdict.allAllowed ? { allowed: true, verdict } : { allowed: false, reason: 'no-entity-permission', verdict }
	}, [entities, permissions])
}
